import { useState, useEffect } from "react";
import NavBar from "../components/NavBar/NavBar";
import About from "./About";
import Experience from "./Experience";
import Portfolio from "./Portfolio";
import Contact from "./Contact";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import kaylee_customized from "../images/kaylee_customized.png";

const RESUME_URL = "/KayleeBurch_Resume.pdf";

export default function Home() {
  const [resumeOpen, setResumeOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Small delay to ensure all sections have rendered their images
    const checkImages = () => {
      const images = document.querySelectorAll("img");
      let loaded = 0;
      const total = images.length;

      if (total === 0) {
        setLoading(false);
        return;
      }

      const onLoad = () => {
        loaded++;
        if (loaded >= total) setLoading(false);
      };

      images.forEach((img) => {
        if (img.complete) {
          onLoad();
        } else {
          img.addEventListener("load", onLoad);
          img.addEventListener("error", onLoad);
        }
      });
    };

    // Wait a tick so all child components have mounted
    const raf = requestAnimationFrame(checkImages);

    // Fallback timeout so the spinner never gets stuck
    const timeout = setTimeout(() => setLoading(false), 5000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, []);

  const handlePrint = () => {
    const printWindow = window.open(RESUME_URL, "_blank");
    printWindow.addEventListener("load", () => {
      printWindow.print();
    });
  };

  return (
    <div className="page-background" id="home">
      {/* Loading Overlay */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(26, 26, 26, 1)',
          opacity: loading ? 1 : 0,
          pointerEvents: loading ? 'auto' : 'none',
          transition: 'opacity 0.4s ease-out',
        }}
      >
        <CircularProgress sx={{ color: '#72efdd' }} size={48} />
      </Box>

      <NavBar />

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          pt: { xs: 12, md: 0 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5} sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={kaylee_customized}
                alt="Kaylee Burch"
                sx={{
                  width: '100%',
                  maxWidth: 420,
                  height: 'auto',
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#72efdd',
                    fontFamily: '"Noto Sans Mono", monospace',
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                  }}
                >
                  {'<h1>'}Hello!{'</h1>'}
                </Typography>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontFamily: '"Ubuntu", sans-serif',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.2,
                    mb: 1,
                  }}
                >
                  I'm <Box component="span" sx={{ color: '#72efdd' }}>Kaylee Burch</Box>,
                </Typography>
                <Typography
                  variant="h3"
                  component="p"
                  sx={{
                    fontFamily: '"Ubuntu", sans-serif',
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 500,
                    fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' },
                    mb: 4,
                  }}
                >
                  Full-Stack Web Developer
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => setResumeOpen(true)}
                  sx={{
                    color: '#72efdd',
                    borderColor: '#72efdd',
                    borderWidth: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#72efdd',
                      backgroundColor: 'rgba(114, 239, 221, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  View Resume
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Resume Modal */}
      <Modal
        open={resumeOpen}
        onClose={() => setResumeOpen(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', md: '70vw' },
            height: '90vh',
            bgcolor: 'rgba(20, 20, 20, 0.98)',
            borderRadius: 2,
            border: '1px solid rgba(114, 239, 221, 0.15)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              borderBottom: '1px solid rgba(114, 239, 221, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              Resume
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                component="a"
                href={RESUME_URL}
                download
                sx={{
                  color: '#72efdd',
                  '&:hover': { backgroundColor: 'rgba(114, 239, 221, 0.1)' },
                }}
                title="Download"
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                onClick={handlePrint}
                sx={{
                  color: '#72efdd',
                  '&:hover': { backgroundColor: 'rgba(114, 239, 221, 0.1)' },
                }}
                title="Print"
              >
                <PrintIcon />
              </IconButton>
              <IconButton
                onClick={() => setResumeOpen(false)}
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* PDF Viewer */}
          <Box sx={{ flex: 1, bgcolor: '#525659' }}>
            <iframe
              src={`${RESUME_URL}#toolbar=0`}
              title="Resume"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </Box>
      </Modal>

      {/* About Section - commented out for now */}
      {/* <div className="section-anchor" id="about"></div>
      <About /> */}

      {/* Portfolio Section */}
      <div className="section-anchor" id="portfolio"></div>
      <Portfolio />

      {/* Experience Section */}
      <div className="section-anchor" id="experience"></div>
      <Experience />

      {/* Contact Section */}
      <div className="section-anchor" id="contact"></div>
      <Contact />
    </div>
  );
}
